import type { ReactNode, Ref } from 'react';

// =====================================================================
// PageHeader — the ONE page-header pattern (design audit 2026-08-28,
// finding P1-9: two competing header patterns, and on nine screens the
// <h1> carried the BUSINESS NAME while the 12 px eyebrow carried the page
// identity — so the biggest text on the page said the same thing on every
// screen and the thing that changed was the smallest).
//
// Operator decisions (2026-08-28), binding:
//   - `title` is the PAGE NAME, never the business name. The business name
//     lives in the sidebar switcher (panel-shell.tsx) and nowhere else.
//   - `eyebrow` is the SECTION the page sits in — Foundation, Visibility,
//     Reputation, Pulse, Account, Reports, Surveys.
//
// `description` and `actions` take nodes so a screen can keep a live URL,
// a status badge or a control next to its title without re-implementing
// the header. `actions` sits on the title ROW (baseline-aligned with the
// h1); `description` spans the full column underneath it, so a long
// sentence never squeezes the buttons.
// =====================================================================

interface PageHeaderProps {
  /** Section this page belongs to — Foundation / Visibility / … */
  eyebrow: string;
  /** The page's own name. Never the business name. */
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  /**
   * Optional focus target. A screen that destroys the card holding the
   * button the customer just pressed (Google Search's disconnect) has to
   * move focus somewhere real — the header is the one element mounted in
   * every state. Passing a ref also makes the <header> programmatically
   * focusable (`tabIndex=-1`); it is never in the tab order.
   */
  ref?: Ref<HTMLElement>;
}

export function PageHeader({ eyebrow, title, description, actions, ref }: PageHeaderProps) {
  return (
    <header
      ref={ref}
      tabIndex={ref ? -1 : undefined}
      className="flex flex-col gap-3 outline-none"
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="eyebrow">{eyebrow}</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">{title}</h1>
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      {/* A plain string is a paragraph; a node is whatever the screen built
          (a link, a badge row) and must not be nested inside a <p>. */}
      {typeof description === 'string' ? (
        <p className="max-w-prose text-sm text-text-secondary">{description}</p>
      ) : description ? (
        <div className="max-w-prose text-sm text-text-secondary">{description}</div>
      ) : null}
    </header>
  );
}
